import { clsx } from 'clsx'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'

export type Tone = 'neutral' | 'positive' | 'negative' | 'caution' | 'info' | 'accent'

export const toneText: Record<Tone, string> = {
  neutral: 'text-ink-2',
  positive: 'text-positive',
  negative: 'text-negative',
  caution: 'text-caution',
  info: 'text-info',
  accent: 'text-accent',
}

export const toneSoft: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2 ring-line',
  positive: 'bg-positive/10 text-positive ring-positive/25',
  negative: 'bg-negative/10 text-negative ring-negative/25',
  caution: 'bg-caution/10 text-caution ring-caution/25',
  info: 'bg-info/10 text-info ring-info/25',
  accent: 'bg-accent/10 text-accent ring-accent/25',
}

/* ── Card ──────────────────────────────────────────────────── */

export function Card({
  children,
  className,
  as: As = 'section',
  padded = true,
}: {
  children: ReactNode
  className?: string
  as?: 'section' | 'div' | 'article'
  padded?: boolean
}) {
  return <As className={clsx('card', padded && 'p-4 sm:p-5', className)}>{children}</As>
}

export function SectionHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={clsx('mb-4 flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <h2 className="h-section">{title}</h2>
        {subtitle ? <p className="mt-1 text-xs leading-relaxed text-ink-3">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/* ── Button ────────────────────────────────────────────────── */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  icon?: ReactNode
  block?: boolean
}

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent/90 active:bg-accent shadow-soft',
  secondary: 'border border-line bg-surface text-ink hover:bg-surface-hover hover:border-line-strong',
  ghost: 'text-ink-2 hover:bg-surface-hover hover:text-ink',
  danger: 'bg-negative text-white hover:bg-negative/90',
  quiet: 'border border-line bg-surface-2 text-ink-2 hover:bg-surface-hover hover:text-ink',
}

const SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-xs',
  md: 'h-9 gap-2 px-3.5 text-sm',
  lg: 'h-11 gap-2 px-5 text-sm',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, block, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'inline-flex select-none items-center justify-center rounded-lg font-medium',
        'transition-[background-color,border-color,color,box-shadow] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  )
})

export function IconButton({
  label,
  icon,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx(
        'inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-2',
        'transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  )
}

/* ── Badge ─────────────────────────────────────────────────── */

export function Badge({
  children,
  tone = 'neutral',
  className,
  title,
}: {
  children: ReactNode
  tone?: Tone
  className?: string
  title?: string
}) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5',
        'text-2xs font-semibold ring-1 ring-inset',
        toneSoft[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/* ── Segmented control ─────────────────────────────────────── */

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  className,
  ariaLabel,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<{ value: T; label: ReactNode; count?: number }>
  size?: 'sm' | 'md'
  className?: string
  ariaLabel?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx(
        'inline-flex w-fit max-w-full items-center gap-0.5 overflow-x-auto rounded-lg',
        'border border-line bg-surface-2 p-0.5 hide-scrollbar',
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={clsx(
              'shrink-0 whitespace-nowrap rounded-md font-medium transition-colors',
              size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
              active
                ? 'bg-surface text-ink shadow-soft'
                : 'text-ink-3 hover:bg-surface-hover hover:text-ink-2',
            )}
          >
            {o.label}
            {o.count !== undefined ? (
              <span className={clsx('ml-1.5 tnum text-2xs', active ? 'text-ink-3' : 'text-ink-3/70')}>
                {o.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/* ── States ────────────────────────────────────────────────── */

export function EmptyState({
  title,
  hint,
  icon,
  action,
  className,
}: {
  title: string
  hint?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={clsx('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      {icon ? <div className="mb-1 text-ink-3/60">{icon}</div> : null}
      <p className="text-sm font-medium text-ink-2">{title}</p>
      {hint ? <p className="max-w-sm text-xs leading-relaxed text-ink-3">{hint}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('skeleton', className)} aria-hidden />
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="読み込み中">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={clsx('h-4', c === 0 ? 'flex-[2]' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('h-4 w-4 animate-spin text-accent', className)} aria-hidden />
}
